import type { NotificationDoc } from '../api/notificationsApi';

/**
 * Maps a notification's `entity_type` to the correct WEB route.
 *
 * The backend stores a single `deep_link` per notification that is shared with
 * the Flutter mobile app, so its value is frequently a mobile route
 * (`/employee/leave/:id`, `/employee/approvals`, …) or a stale/nonexistent web
 * route (`/manager/approvals/:id`, `/manager/employees/:enroll_id`). Navigating
 * to those raw on the web 404s (notes7 #3). The web client therefore derives its
 * own destination from `entity_type` and falls back to the notification center.
 *
 * No dedicated per-request detail pages exist on web, so each type routes to its
 * section/list page (confirmed product decision).
 */
const ENTITY_ROUTES: Record<string, string> = {
  vacation: '/attendance-management/leaves',
  rnp: '/attendance-management/rewards',
  holiday: '/attendance-management/holidays',
  fingerprint_log: '/work-time/attendance',
  news: '/students/news',
};

/** Prefixes of routes that actually exist in the web SPA router. */
const SAFE_WEB_PREFIXES = [
  '/attendance-management',
  '/work-time',
  '/students',
  '/employees',
  '/staff',
  '/reports',
  '/devices',
  '/settings',
  '/notifications',
  '/dashboard',
];

export function resolveNotificationRoute(notif: NotificationDoc): string {
  const entity = notif.data?.entity_type;
  if (entity && ENTITY_ROUTES[entity]) return ENTITY_ROUTES[entity];

  // Tertiary: honour a stored deep_link only when it's clearly a valid web path
  // (never a mobile `/employee/*` or a stale `/manager/*` route).
  const raw = notif.data?.deep_link;
  if (raw) {
    const path = raw.startsWith('/') ? raw : `/${raw}`;
    if (
      SAFE_WEB_PREFIXES.some(
        (p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`),
      )
    ) {
      return path;
    }
  }

  // Anything unmapped (broadcast / system / unknown) → the notification center.
  return '/notifications';
}
